import { z } from "zod";
import { BET_PANELS, CURRENCY_CODE_REGEX, EDGE_MODES, GAME_BOUNDS, LICENSE_TIERS, PF_MODES, RENDER_MODES, ROUND_PHASES, SHA256_HEX_REGEX, } from "../constants.js";
// ─── primitives ───────────────────────────────────────────────────────────────
export const CurrencyCode = z.string().regex(CURRENCY_CODE_REGEX);
export const Money = z.number().finite().nonnegative();
/** Multipliers are always 2-decimal floats ≥ 1.00 (1.00 = instant crash). */
export const Multiplier = z.number().finite().min(1).multipleOf(0.01);
export const Timestamp = z.number().int().nonnegative(); // Unix ms (server clock)
export const Id = z.string().min(1).max(64);
// ─── public game config (sent in state:sync) ─────────────────────────────────
export const PublicGameConfig = z.object({
    fun: z.boolean(),
    /** Envato license tier — `regular` locks every real-money feature (see docs/11). */
    licenseTier: z.enum(LICENSE_TIERS),
    renderMode: z.enum(RENDER_MODES),
    allowPlayerRenderModeChange: z.boolean().default(false),
    availableRenderModes: z.array(z.enum(RENDER_MODES)).min(1),
    growthK: z.number().min(GAME_BOUNDS.growthK.min).max(GAME_BOUNDS.growthK.max),
    houseEdge: z.number().min(GAME_BOUNDS.houseEdge.min).max(GAME_BOUNDS.houseEdge.max),
    edgeMode: z.enum(EDGE_MODES),
    pfMode: z.enum(PF_MODES),
    maxMultiplier: z.number().min(GAME_BOUNDS.maxMultiplier.min).max(GAME_BOUNDS.maxMultiplier.max),
    bettingMs: z.number().int().min(GAME_BOUNDS.bettingMs.min).max(GAME_BOUNDS.bettingMs.max),
    crashPauseMs: z.number().int().min(GAME_BOUNDS.crashPauseMs.min).max(GAME_BOUNDS.crashPauseMs.max),
    currencies: z.array(z.object({
        code: CurrencyCode,
        decimals: z.number().int().min(0).max(8),
        minBet: Money,
        maxBet: Money,
        maxWinPerRound: Money,
    })),
    panels: z.number().int().min(1).max(2),
    chatEnabled: z.boolean(),
    activeThemeSlug: z.string().min(1),
});
// ─── rounds ──────────────────────────────────────────────────────────────────
export const RoundPhaseSchema = z.enum(ROUND_PHASES);
export const HistoryEntry = z.object({
    roundId: Id,
    seq: z.number().int().nonnegative(),
    crashAt: Multiplier,
    crashedAt: Timestamp,
});
/** Public (anonymised) view of a bet, as broadcast to every client. */
export const PublicBet = z.object({
    betId: Id,
    nick: z.string().min(1).max(32), // already masked, e.g. "d***3"
    avatar: z.string().min(1).max(64),
    isBot: z.boolean(),
    panel: z.enum(BET_PANELS),
    amount: Money,
    currency: CurrencyCode,
    /** present once cashed out */
    multiplier: Multiplier.optional(),
    win: Money.optional(),
});
/** The caller's own bet, with private fields. */
export const MyBet = PublicBet.extend({
    autoCashout: Multiplier.nullable(),
    status: z.enum(["pending", "active", "won", "lost", "cancelled"]),
    placedAt: Timestamp,
    cashoutAt: Timestamp.nullable(),
});
export const RoundState = z.object({
    roundId: Id,
    seq: z.number().int().nonnegative(),
    phase: RoundPhaseSchema,
    /** SHA-256 commitment of the server seed, known from BETTING on. */
    serverSeedHash: z.string().regex(SHA256_HEX_REGEX),
    /** When BETTING ends (ms). */
    bettingEndsAt: Timestamp.nullable(),
    /** When FLYING started (ms). null until then. Clients compute m(t) from this. */
    startedAt: Timestamp.nullable(),
    /** Set only in CRASHED/RESOLVING. */
    crashAt: Multiplier.nullable(),
});
// ─── client → server payloads ───────────────────────────────────────────────
export const PlaceBetInput = z.object({
    panel: z.enum(BET_PANELS),
    amount: z.number().finite().positive(),
    currency: CurrencyCode,
    autoCashout: Multiplier.min(1.01).nullable().default(null),
    /** Client-generated idempotency key (uuid). */
    clientRef: z.string().min(8).max(64),
});
export const CancelBetInput = z.object({ betId: Id });
export const CashoutInput = z.object({
    betId: Id,
    /** Client-observed multiplier, informational only (server decides). */
    clientMultiplier: Multiplier.optional(),
});
export const ClientSeedInput = z.object({ seed: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/) });
// ─── server → client payloads ───────────────────────────────────────────────
export const StateSync = z.object({
    serverTime: Timestamp,
    config: PublicGameConfig,
    round: RoundState,
    history: z.array(HistoryEntry),
    bets: z.array(PublicBet),
    myBets: z.array(MyBet),
    onlinePlayers: z.number().int().nonnegative(),
});
export const RoundBettingEvent = z.object({
    roundId: Id,
    seq: z.number().int().nonnegative(),
    serverSeedHash: z.string().regex(SHA256_HEX_REGEX),
    bettingEndsAt: Timestamp,
    serverTime: Timestamp,
});
export const RoundStartEvent = z.object({
    growthK: PublicGameConfig.shape.growthK.optional(),
    roundId: Id,
    startedAt: Timestamp,
    serverTime: Timestamp,
});
export const RoundTickEvent = z.object({
    roundId: Id,
    serverTime: Timestamp,
});
export const RoundCrashEvent = z.object({
    roundId: Id,
    seq: z.number().int().nonnegative(),
    crashAt: Multiplier,
    crashedAt: Timestamp,
    /** Provably-fair reveal. */
    pf: z.object({
        mode: z.enum(PF_MODES),
        serverSeed: z.string().min(16),
        /** chain mode: public salt (e.g. a Bitcoin block hash); players mode: the 3 client seeds. */
        publicSalt: z.string().optional(),
        clientSeeds: z.array(z.string()).optional(),
        hash: z.string().regex(/^[0-9a-f]{64}$|^[0-9a-f]{128}$/),
    }),
});
/** Completed round plus its pre-flight commitment, for historical verification. */
export const RoundReveal = RoundCrashEvent.extend({
    serverSeedHash: z.string().regex(SHA256_HEX_REGEX),
}).refine((round) => round.pf.mode === "chain"
    ? Boolean(round.pf.publicSalt) && round.pf.hash.length === 64
    : round.pf.clientSeeds?.length === 3 && round.pf.hash.length === 128, { message: "Incomplete provably-fair reveal" });
export const BetsBatchEvent = z.object({
    roundId: Id,
    placed: z.array(PublicBet),
    cashed: z.array(PublicBet),
    cancelled: z.array(Id),
});
export const BalanceEvent = z.object({
    currency: CurrencyCode,
    balance: Money,
    locked: Money,
    fun: z.boolean(),
});
export const OnlineEvent = z.object({ onlinePlayers: z.number().int().nonnegative() });
export const ServerTimeEvent = z.object({ clientSentAt: Timestamp, serverTime: Timestamp });
export const NoticeEvent = z.object({
    level: z.enum(["info", "warning", "error"]),
    /** i18n key, e.g. "notices.maintenanceSoon" */
    key: z.string().min(1),
    params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
//# sourceMappingURL=game.js.map